import React, { useEffect, useState } from "react";
import { ConfigProvider, Layout, theme as antdTheme } from "antd";
import DefaultLayoutContent from "./Content";
import Topbar from "./Topbar";
import AppBar from "./AppBar";
import BottomTabBar from "./BottomTabBar";
import DefaultLayoutSidebar from "./Sidebar";
import Footer from "./Footer";
import { FilterBar } from "../../components/filters";
import ProfileMenu from "../../components/ProfileMenu/ProfileMenu";
import useUsuario from "../../../core/hooks/UseUsuario";
import useIsMobile from "../../../core/hooks/useIsMobile";
import AuthService from "../../auth/Authorization.service";

// Antd derives colorPrimaryHover/colorPrimaryActive etc. by running this seed
// through its own color-math (tinycolor), which cannot parse a
// `rgb(var(--c-primary))` string — so these two are literal hex, not a token
// reference. Both are theme-invariant in src/index.css (identical in :root
// and html.dark), so no fidelity is lost: #1ACB7B = --c-primary, #061523 =
// --c-primary-contrast (8.67:1 against it).
//
// The Button component tokens below, by contrast, are used as plain CSS
// property values (background, borderColor, color) and never run through
// colour-derivation math, so the `rgb(var(--c-*))` references work and stay
// theme-reactive with no JS-side tracking needed (same pattern as
// SIDEBAR_MENU_THEME in ./sidebarNavConfig.tsx).
// The CSS side of this split lives in src/index.css: `--c-primary` is blue
// product-wide, and html[data-palette="home"] restores the green on /home.
// Antd cannot take part in that cascade — it needs a real hex to run through
// tinycolor (see the note above) — so the same palettes are spelled out here
// and picked by (pathname, theme). Keep these in step with the CSS blocks; a
// drift shows up as an antd button whose fill disagrees with the Tailwind
// button beside it.
//
// Unlike the green, the blue is NOT theme-invariant: dark mode keeps the
// lighter #4A86E0 fill but carries the near-black label, because white on that
// blue measures only 3.63:1. See the comment on --c-primary in html.dark.
const PALETTE = {
  blue: {
    light: { colorPrimary: '#2F6CD2', colorTextLightSolid: '#FFFFFF' },
    dark: { colorPrimary: '#4A86E0', colorTextLightSolid: '#061523' },
  },
  // #1ACB7B reads the same in both themes against the #061523 foreground.
  home: {
    light: { colorPrimary: '#1ACB7B', colorTextLightSolid: '#061523' },
    dark: { colorPrimary: '#1ACB7B', colorTextLightSolid: '#061523' },
  },
};

const antdThemeTokens = {
  token: {
    colorPrimary: PALETTE.blue.light.colorPrimary,
    colorTextLightSolid: PALETTE.blue.light.colorTextLightSolid,
  },
  components: {
    Button: {
      borderRadius: 10,
      borderRadiusLG: 10,
      borderRadiusSM: 10,
      defaultBg: 'rgb(var(--c-surface))',
      defaultBorderColor: 'rgb(var(--c-border))',
      defaultColor: 'rgb(var(--c-text))',
      defaultHoverBg: 'rgb(var(--c-surface-hover))',
      defaultHoverBorderColor: 'rgb(var(--c-border-strong))',
      defaultHoverColor: 'rgb(var(--c-text))',
      defaultActiveBg: 'rgb(var(--c-surface-hover))',
      defaultActiveBorderColor: 'rgb(var(--c-border-strong))',
      defaultActiveColor: 'rgb(var(--c-text))',
    },
  },
};

/**
 * hideNav strips the whole chrome (sidebar, filter, and the header's logged-in
 * affordances). hideFilter keeps the chrome but drops just the question filter —
 * for pages that live under the nav but aren't driven by the question bank.
 */
function DefaultLayout(props: {
  children: React.ReactNode;
  hideNav?: boolean;
  hideFilter?: boolean;
}) {
  const { usuario, getUser } = useUsuario();

  const isMobile = useIsMobile();

  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains("dark")
  );

  // Which palette <html> is carrying — set by RootLayout (src/app/MyRoutes.tsx)
  // from the pathname. Mirrored into state, like isDark, because antd's theme
  // is a JS prop: reading the attribute during render would not re-run when it
  // changes on navigation, leaving antd's buttons on the previous route's hue.
  const [isHomePalette, setIsHomePalette] = useState(
    () => document.documentElement.getAttribute("data-palette") === "home"
  );

  useEffect(() => {
    const read = () => {
      setIsDark(document.documentElement.classList.contains("dark"));
      setIsHomePalette(
        document.documentElement.getAttribute("data-palette") === "home"
      );
    };
    const obs = new MutationObserver(read);
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "data-palette"],
    });
    // RootLayout's effect and this one both run on mount, in an order React
    // does not promise, so re-read once after subscribing rather than trusting
    // the useState initialiser to have seen the attribute.
    read();
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (AuthService.getAccessToken() && !usuario?.id) {
      getUser();
    }
  }, []);

  // Option fetching moved into useFilterOptions, which FilterBar owns — the
  // layout no longer needs to know what a testing agency is.

  const bg = isDark ? "rgb(var(--c-canvas))" : undefined;

  // /home keeps the brand green, every other route is blue — the same split
  // src/index.css applies via html[data-palette="home"]. Keyed off that same
  // attribute rather than re-deriving the pathname, so the CSS and antd can
  // never disagree about which palette is in force.
  const palette = isHomePalette ? PALETTE.home : PALETTE.blue;
  const paletteToken = isDark ? palette.dark : palette.light;

  return (
    <ConfigProvider
      theme={{
        ...antdThemeTokens,
        token: { ...antdThemeTokens.token, ...paletteToken },
        algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
      }}
    >
    {/*
      App shell: the rail owns the full height of the viewport and the Topbar
      sits beside it, as the first row of the content column.

      The Sider used to live one level down, under the Topbar, so it started at
      y=72 and — because the document was the scroller — scrolled away with the
      page (measured on /home: documento 2309px, rail saindo de vista junto).
      Three things make it stay now:

        1. the Sider is a direct child of this outer Layout, which is what antd
           looks for to switch itself to flex-direction: row (it inspects
           children for a Sider, and finds it through the DefaultLayoutSidebar
           wrapper — confirmed by the ant-layout-has-sider class);
        2. this outer Layout is overflow:hidden at exactly 100vh, so nothing
           can push the rail or the Topbar out of the viewport;
        3. the scrolling moved inside, to the column below the Topbar.

      minHeight/minWidth 0 are load-bearing, not tidying: a flex child defaults
      to min-*: auto, which lets it grow past the container instead of
      scrolling (the column) or clipping wide content like the question tables
      (the row).
    */}
    <Layout style={{ height: "100vh", background: bg, overflow: "hidden" }}>
      {!props.hideNav && !isMobile && <DefaultLayoutSidebar collapsed={false} />}

      <Layout id={"PageLayout"} style={{ background: bg, minWidth: 0 }}>
        {isMobile ? (
          <AppBar hideNav={props.hideNav} />
        ) : (
          <Topbar hideNav={props.hideNav} />
        )}

        <Layout
          style={{
            background: bg,
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
            minHeight: 0,
          }}
        >
          {!props.hideNav && !props.hideFilter && (
            <div className="pt-4">
              <FilterBar />
            </div>
          )}
          <DefaultLayoutContent>{props.children}</DefaultLayoutContent>
          <Footer hideNav={props.hideNav} />
        </Layout>

        {!props.hideNav && isMobile && <BottomTabBar />}
      </Layout>

      {/*
        Menu de perfil — fixo no canto inferior esquerdo, que neste produto é
        dentro do rail. Fica FORA do `Sider` de propósito: é `position: fixed`,
        então não precisa do fluxo do rail, e assim continua de fora das
        varreduras de cor do e2e que têm o `.ant-layout-sider` como raiz.

        Só no desktop: no mobile o rodapé é da BottomTabBar, e perfil e saída
        vivem no drawer do App Bar (ver `includeAccountItems` em
        sidebarNavConfig). Um avatar flutuante ali brigaria com a barra de abas.
      */}
      {!props.hideNav && !isMobile && <ProfileMenu />}
    </Layout>
    </ConfigProvider>
  );
}

export default DefaultLayout;
